import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { adminAgenciesApi, adminUsersApi } from "@/api/admin";
import { Alert } from "@/components/Alert";
import { Field } from "@/components/Field";
import { FormSelect } from "@/components/FormSelect";
import { FullPageSpinner } from "@/components/FullPageSpinner";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { useRecordForm } from "@/hooks/useRecordForm";

const BLANK_USER = {
  email: "",
  first_name: "",
  last_name: "",
  phone: "",
  role: "coordinator",
  agency: "",
  is_superuser: false,
  is_active: true,
  password: "",
};

const ROLE_OPTIONS = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Administrator" },
  { value: "coordinator", label: "Care coordinator" },
  { value: "caregiver", label: "Caregiver" },
];

const SUPERUSER_OPTIONS = [
  { value: "false", label: "No (Linked to an Organization)" },
  { value: "true", label: "Yes (Global Admin, No Organization)" },
];

const STATUS_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    isNew,
    values,
    setValue,
    handleChange,
    isLoading,
    isDirty,
    discard,
    error,
  } = useRecordForm({
    resource: adminUsersApi,
    id,
    blankValues: BLANK_USER,
    redirectTo: "/admin/users",
  });

  const [agenciesList, setAgenciesList] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    adminAgenciesApi
      .list({ page_size: 100 })
      .then((data) => {
        setAgenciesList(data.results || []);
      })
      .catch((err) => {
        console.error("Failed to load agencies:", err);
      })
      .finally(() => {
        setLoadingAgencies(false);
      });
  }, []);

  const saveDisabled = isSaving || (!isNew && !isDirty);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isNew && !isDirty) return;
    setIsSaving(true);
    setSaveError(null);
    setFieldErrors({});

    const payload = { ...values };
    if (payload.is_superuser) {
      payload.agency = null;
      payload.is_staff = true;
    } else {
      payload.agency = payload.agency || null;
      payload.is_staff = false;
    }
    // Remove blank password on edit
    if (!payload.password && !isNew) {
      delete payload.password;
    }

    try {
      if (isNew) {
        await adminUsersApi.create(payload);
      } else {
        await adminUsersApi.update(id, payload);
      }
      navigate("/admin/users", { replace: true });
    } catch (apiError) {
      setSaveError(apiError.fieldErrors ? null : apiError.message);
      setFieldErrors(apiError.fieldErrors ?? {});
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to permanently delete this user account?")) {
      setIsSaving(true);
      adminUsersApi
        .remove(id)
        .then(() => {
          navigate("/admin/users", { replace: true });
        })
        .catch((apiError) => {
          setSaveError(apiError.message);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
  };

  const handleDiscard = () => {
    discard();
    setSaveError(null);
    setFieldErrors({});
  };

  if (isLoading || loadingAgencies) {
    return <FullPageSpinner label="Loading user details" />;
  }

  const agencyOptions = [
    { value: "", label: "Select an organization..." },
    ...agenciesList.map((agency) => ({ value: agency.id, label: agency.name })),
  ];

  return (
    <PageShell>
      <PageHeader
        title={isNew ? "New user" : values.email}
        description={
          isNew
            ? "Create a new user account and assign roles and tenancy."
            : "Edit user account details."
        }
      />

      <PageBody>
        <Alert>{error || saveError}</Alert>

        <form onSubmit={handleSubmit} noValidate>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Account details</CardTitle>
              <CardDescription>
                Email, credentials, role, and organization assignment for this user.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email address" name="email" error={fieldErrors.email} className="sm:col-span-2">
                  {(props) => (
                    <Input
                      {...props}
                      type="email"
                      required
                      value={values.email}
                      onChange={handleChange}
                      placeholder="e.g. user@example.com"
                      disabled={!isNew}
                    />
                  )}
                </Field>

                <Field
                  label="Password"
                  name="password"
                  error={fieldErrors.password}
                  hint={!isNew ? "Leave blank to keep current password." : undefined}
                  className="sm:col-span-2"
                >
                  {(props) => (
                    <PasswordInput
                      {...props}
                      required={isNew}
                      value={values.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                  )}
                </Field>

                <Field label="First name" name="first_name" error={fieldErrors.first_name}>
                  {(props) => (
                    <Input
                      {...props}
                      type="text"
                      value={values.first_name}
                      onChange={handleChange}
                      placeholder="e.g. John"
                    />
                  )}
                </Field>

                <Field label="Last name" name="last_name" error={fieldErrors.last_name}>
                  {(props) => (
                    <Input
                      {...props}
                      type="text"
                      value={values.last_name}
                      onChange={handleChange}
                      placeholder="e.g. Doe"
                    />
                  )}
                </Field>

                <Field label="Phone number" name="phone" error={fieldErrors.phone}>
                  {(props) => (
                    <Input
                      {...props}
                      type="tel"
                      value={values.phone}
                      onChange={handleChange}
                      placeholder="e.g. +1 555 0100"
                    />
                  )}
                </Field>

                <Field label="Role" name="role" error={fieldErrors.role}>
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={values.role}
                      onValueChange={(v) =>
                        handleChange({ target: { name: props.name, value: v } })
                      }
                      options={ROLE_OPTIONS}
                      placeholder="Select role…"
                    />
                  )}
                </Field>

                <Field
                  label="Superuser status"
                  name="is_superuser"
                  error={fieldErrors.is_superuser}
                  className="sm:col-span-2"
                >
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={values.is_superuser.toString()}
                      onValueChange={(v) => {
                        const isSuper = v === "true";
                        setValue("is_superuser", isSuper);
                        if (isSuper) {
                          setValue("agency", "");
                        }
                      }}
                      options={SUPERUSER_OPTIONS}
                      placeholder="Select…"
                    />
                  )}
                </Field>

                {!values.is_superuser && (
                  <Field
                    label="Organization (Agency)"
                    name="agency"
                    error={fieldErrors.agency}
                    className="sm:col-span-2"
                  >
                    {(props) => (
                      <FormSelect
                        id={props.id}
                        name={props.name}
                        value={values.agency || ""}
                        onValueChange={(v) =>
                          handleChange({ target: { name: props.name, value: v } })
                        }
                        options={agencyOptions}
                        placeholder="Select an organization..."
                      />
                    )}
                  </Field>
                )}

                <Field label="Account status" name="is_active" error={fieldErrors.is_active}>
                  {(props) => (
                    <FormSelect
                      id={props.id}
                      name={props.name}
                      value={values.is_active.toString()}
                      onValueChange={(v) => setValue("is_active", v === "true")}
                      options={STATUS_OPTIONS}
                      placeholder="Select status…"
                    />
                  )}
                </Field>
              </div>
            </CardContent>
            <CardFooter className="flex flex-wrap items-center gap-2 border-t border-border px-4 pt-4">
              <Button type="submit" disabled={saveDisabled}>
                {isSaving ? "Saving…" : "Save user"}
              </Button>
              {!isNew ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={!isDirty || isSaving}
                  onClick={handleDiscard}
                >
                  Discard
                </Button>
              ) : null}
              <Button type="button" variant="ghost" asChild>
                <Link to="/admin/users">Cancel</Link>
              </Button>
              {!isNew ? (
                <Button
                  type="button"
                  variant="destructive"
                  className="ml-auto"
                  onClick={handleDelete}
                  disabled={isSaving}
                >
                  Delete user
                </Button>
              ) : null}
            </CardFooter>
          </Card>
        </form>
      </PageBody>
    </PageShell>
  );
}
